import { type ReactNode } from 'react';
import format from 'string-format';

import { type RequestContext } from '~/types/RequestContext';

import { BettingInsightsComponent } from '../BettingInsightsConfiguration';
import {
	type BettingInsightsContent,
	type BettingInsightsResult,
	type BettingInsightsResultAttribute,
	type EsIndex,
} from '../types';
import { formatDate } from '../utils/formatDate';

export interface BettingInsightsTitleConfig<
	TIndex extends EsIndex,
	TResult extends BettingInsightsResult<TIndex> = BettingInsightsResult<TIndex>,
	TContent extends
		BettingInsightsContent<TIndex>[] = BettingInsightsContent<TIndex>[],
	TAttribute extends
		BettingInsightsResultAttribute<TIndex> = BettingInsightsResultAttribute<TIndex>,
> {
	defaultFormat: string;
	getFormatOverride?: ({
		content,
		result,
	}: {
		content: TContent;
		result: TResult;
	}) => string | undefined;
	getResultOverrides?: (
		this: BettingInsightsTitle<TIndex, TResult, TContent, TAttribute>,
		{
			content,
			result,
		}: {
			content: TContent;
			result: TResult;
		},
	) => Partial<Record<TAttribute, unknown>> | undefined;
	render?: ({
		content,
		result,
	}: {
		content: TContent;
		result: TResult;
	}) => ReactNode;
}

export class BettingInsightsTitle<
		TIndex extends EsIndex,
		TResult extends
			BettingInsightsResult<TIndex> = BettingInsightsResult<TIndex>,
		TContent extends
			BettingInsightsContent<TIndex>[] = BettingInsightsContent<TIndex>[],
		TAttribute extends
			BettingInsightsResultAttribute<TIndex> = BettingInsightsResultAttribute<TIndex>,
	>
	extends BettingInsightsComponent
	implements BettingInsightsTitleConfig<TIndex, TResult, TContent, TAttribute>
{
	defaultFormat: string;
	getFormatOverride?: ({
		content,
		result,
	}: {
		content: TContent;
		result: TResult;
	}) => string | undefined;
	getResultOverrides?: ({
		content,
		result,
	}: {
		content: TContent;
		result: TResult;
	}) => Partial<Record<TAttribute, unknown>> | undefined;

	constructor({
		defaultFormat: formatString,
		getFormatOverride,
		getResultOverrides,
		render,
		...requestContext
	}: BettingInsightsTitleConfig<TIndex, TResult, TContent, TAttribute> &
		RequestContext) {
		super(requestContext);
		this.defaultFormat = formatString;
		this.getFormatOverride = getFormatOverride;
		this.getResultOverrides = getResultOverrides?.bind(this);
		this.render = render?.bind(this) ?? this.render.bind(this);
	}

	render({
		content,
		result,
	}: {
		content: TContent;
		result: TResult;
	}): ReactNode {
		return format(
			(this.getFormatOverride && this.getFormatOverride({ content, result })) ??
				this.defaultFormat,
			{
				...result,
				...content[0],
				subeventStartTime: formatDate(
					result.subeventStartTime,
					undefined,
					this.repubConfig,
				),
				...(this.getResultOverrides
					? this.getResultOverrides({ content, result })
					: {}),
			},
		);
	}

	static withRequestContext<TIndex extends EsIndex>(
		requestContext: RequestContext,
		config: BettingInsightsTitleConfig<TIndex>,
	) {
		return new BettingInsightsTitle<TIndex>({
			...config,
			...requestContext,
		});
	}
}
